/**
 * Service for date validation operations
 */

import type { DateValidationResult, CampaignDates } from '../types/DurationTypes';

export class DateValidationService {
  /**
   * Get minimum allowed date (48 hours from now, or now if admin bypass)
   */
  static getMinimumDate(isAdminBypass: boolean = false): Date {
    if (isAdminBypass) {
      // Admin can select any date from now
      return new Date();
    }
    const minDate = new Date();
    minDate.setHours(minDate.getHours() + 48);
    return minDate;
  }

  /**
   * Validate start date selection
   */
  static validateStartDate(
    selectedDate: Date, 
    currentEndDate: Date | null, 
    minDate: Date
  ): DateValidationResult {
    // Check if date is before minimum allowed date
    if (selectedDate < minDate) {
      console.warn(`Selected start date ${selectedDate.toISOString()} is before min date ${minDate.toISOString()}`);
      return { 
        isValid: false, 
        error: 'Start date must be at least 48 hours from now' 
      };
    }

    // Check if we need to clear end date
    const shouldClearEndDate = currentEndDate !== null && selectedDate > currentEndDate;
    
    if (shouldClearEndDate) {
      console.log('Start date is after current end date, will clear end date');
    }

    return { 
      isValid: true, 
      shouldClearEndDate 
    };
  }

  /**
   * Validate end date selection
   */
  static validateEndDate(
    selectedDate: Date, 
    currentStartDate: Date | null
  ): DateValidationResult {
    if (!currentStartDate) {
      console.warn('Cannot select end date without start date');
      return { 
        isValid: false, 
        error: 'Please select a start date first' 
      };
    }

    if (selectedDate < currentStartDate) {
      console.warn(`Selected end date ${selectedDate.toISOString()} is before start date ${currentStartDate.toISOString()}`);
      return { 
        isValid: false, 
        error: 'End date must be after start date' 
      };
    }

    return { isValid: true };
  }

  /**
   * Validate loaded dates from storage
   */
  static validateLoadedDates(
    startDate: Date | null, 
    endDate: Date | null
  ): { validStartDate: Date | null; validEndDate: Date | null } {
    const now = new Date();
    
    let validStartDate = startDate;
    let validEndDate = endDate;

    // Validate start date
    if (startDate && startDate < now) {
      console.warn('Loaded start date is in the past, clearing');
      validStartDate = null;
    }

    // Validate end date
    if (endDate && startDate) {
      if (endDate < startDate || startDate < now) {
        console.warn('Loaded end date is invalid, clearing');
        validEndDate = null;
      }
    } else if (endDate && !startDate) {
      console.warn('End date exists without start date, clearing');
      validEndDate = null;
    }

    return { validStartDate, validEndDate };
  }

  /**
   * Check if two dates represent the same day
   */
  static isSameDay(date1: Date, date2: Date): boolean {
    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate()
    );
  }

  /**
   * Create a date for start of day (12:00:00)
   */
  static createStartOfDay(year: number, month: number, day: number): Date {
    return new Date(year, month, day, 12, 0, 0);
  }

  /**
   * Create a date for end of day (23:59:59)
   */
  static createEndOfDay(year: number, month: number, day: number): Date {
    return new Date(year, month, day, 23, 59, 59, 999);
  }
}